import { provide, inject, computed, ref, onUpdated, onBeforeUnmount, getCurrentInstance, type ComponentPublicInstance, type Ref, type InjectionKey, type WritableComputedRef, watch, type UnwrapRef, toValue, type MaybeRefOrGetter } from 'vue';
import { tryOnMounted, isDefined, type RemovableRef, type ArgumentsType } from '@vueuse/shared';
import { useElementSize, useLocalStorage } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { pop } from './utils';
import type { RemovableWritableComputedRef } from './typeutils';

type ChildrenTracker<T extends ComponentPublicInstance> = {
  register: (item: T) => void;
  unregister: (item: T) => void;
};

/** InjectionKey tipizzato per il pattern children tracker (registrazione/de-registrazione componenti figli) */
export type ChildrenTrackerInjectionKey<T extends ComponentPublicInstance = ComponentPublicInstance> = InjectionKey<ChildrenTracker<T>>;

const ChildrenTrackerSymbol = Symbol('Children tracker provide/inject symbol') as ChildrenTrackerInjectionKey<ComponentPublicInstance>;

/**
 * Fornisce un tracker per registrare e tracciare i componenti figli montati
 * @template T - Tipo del componente figlio
 * @param symbol - Symbol o InjectionKey per il provide/inject
 * @returns Ref con array dei componenti figli registrati
 */
export function provideChildrenTracker<T extends ComponentPublicInstance = ComponentPublicInstance>(symbol: symbol | ChildrenTrackerInjectionKey<T>) {
  const items: Ref<T[]> = ref([]);

  provide(symbol || ChildrenTrackerSymbol, {
    // inserito in ordine di DOM: l'ordine di mount non è garantito (es. contenuto rimontato dentro un pf-modal)
    register: (item: T) => {
      const el: unknown = item.$el;
      const idx = el instanceof Node
        ? items.value.findIndex(i => i.$el instanceof Node && Boolean(el.compareDocumentPosition(i.$el) & Node.DOCUMENT_POSITION_FOLLOWING))
        : -1;
      if (idx >= 0) {
        items.value.splice(idx, 0, item);
      } else {
        items.value.push(item);
      }
    },

    unregister: (item: T) => {
      const idx = items.value.findIndex(i => i === item);
      if (idx >= 0) {
        items.value.splice(idx, 1);
      }
    },
  });

  return items;
}

/**
 * Registra il componente corrente nel tracker padre e gestisce auto-unregister on unmount
 * @template T - Tipo del componente
 * @param symbol - Symbol o InjectionKey usato dal tracker padre
 * @returns Il tracker iniettato, o null se non presente
 */
export function useChildrenTracker<T extends ComponentPublicInstance>(symbol: symbol | ChildrenTrackerInjectionKey<T>) {
  const tracker = inject(symbol || ChildrenTrackerSymbol, null);

  if (tracker) {
    tryOnMounted(() => {
      const instance = getCurrentInstance();
      if (instance?.proxy) {
        tracker.register(instance.proxy as T);
      }
    });

    onBeforeUnmount(() => {
      const instance = getCurrentInstance();
      if (instance?.proxy) {
        tracker.unregister(instance.proxy as T);
      }
    });
  }

  return tracker;
}

/**
 * Crea una prop v-model gestita con fallback interno e supporto per modifier
 * @template G - Tipo del getter
 * @template S - Tipo del setter (default: stesso di G)
 * @param name - Nome della prop
 * @param value - Valore di fallback quando la prop non è definita
 * @param modifier - Funzione opzionale per modificare il valore prima di salvarlo
 * @returns WritableComputedRef che legge dalla prop o dal ref interno e emette eventi update
 */
export function useManagedProp<G, S = G>(name: string, value: G | S, modifier?: (to: G | S) => G): WritableComputedRef<G, S> {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('missing component instance');
  }

  const inner = ref(value);

  const watchStop = watch(() => instance.props[name] as G, (newvalue) => {
    inner.value = isDefined(newvalue) ? newvalue : value as UnwrapRef<G>;
  });

  onBeforeUnmount(watchStop);

  return computed({
    get(): G {
      return isDefined(instance.props[name]) ? (instance.props[name] as any) : inner.value;
    },
    set(to: S) {
      if (inner.value || !isDefined(instance.props[name])) {
        inner.value = modifier ? modifier(to) : to;
      }
      instance.emit(`update:${name}`, to);
    },
  });
}

/** Opzioni per configurare il comportamento di useElementOverflow */
export type ElementOverflowOptions = {
  direction?: 'height' | 'width' | 'both';
  watchSize?: boolean;
};

/**
 * Rileva se un elemento HTML ha overflow (contenuto che eccede le dimensioni)
 * @param element - Ref all'elemento HTML da monitorare
 * @param options - Opzioni per configurare direzione e watch del resize
 * @returns Ref booleano che indica se l'elemento ha overflow
 */
export function useElementOverflow(element: Ref<HTMLElement | undefined>, options?: ElementOverflowOptions): Ref<boolean> {
  const direction = options?.direction ?? 'both';

  const overflowing = ref(false);
  const size = useElementSize(element);

  const testElementOverflow = () => {
    if (!window || !element.value) {
      overflowing.value = false;
      return;
    }

    if (direction === 'both' || direction === 'width') {
      overflowing.value = element.value.scrollWidth > element.value.clientWidth;
      if (overflowing.value) {
        return;
      }
    }

    if (direction === 'both' || direction === 'height') {
      overflowing.value = element.value.scrollHeight > element.value.clientHeight;
    }
  };

  tryOnMounted(testElementOverflow);
  onUpdated(testElementOverflow);

  if (options?.watchSize) {
    watch(() => {
      if (direction === 'both') {
        return size;
      }

      if (direction === 'width') {
        return size.width.value;
      }

      if (direction === 'height') {
        return size.height.value;
      }
    }, testElementOverflow);
  }

  return overflowing;
}

type InitialResettableState = Record<string, any>;
type ResettableState<T> = {
  [K in keyof T]: Ref<T[K]>;
};

/**
 * Crea uno state reattivo con funzione reset per ripristinare i valori iniziali
 * @template T - Tipo dell'oggetto state iniziale
 * @param initialState - Oggetto con i valori iniziali dello state
 * @returns Oggetto con state (refs) e funzione reset
 */
export function useResettableState<T extends InitialResettableState>(initialState: T): {
  state: ResettableState<T>,
  reset: (exclude?: string[]) => void,
} {
  const state = {} as ResettableState<T>;
  for (const key in initialState) {
    state[key] = ref(initialState[key]);
  }

  const reset = (exclude?: string[]) => {
    for (const key in initialState) {
      if (!exclude?.includes(key)) {
        state[key].value = initialState[key];
      }
    }
  };

  return {
    state,
    reset,
  };
}

/**
 * Crea un computed sincronizzato con un parametro della query string della route
 * @param name - Nome del parametro nella query string
 * @param def - Valore di default (opzionale)
 * @param numeric - Se true, converte il valore in number
 * @returns WritableComputedRef che legge/scrive nella query string
 */
export function useRouteQueryParam<T extends number | undefined>(name: string, def: T, numeric: true): WritableComputedRef<number | T>;
export function useRouteQueryParam<T extends string | undefined>(name: string, def: T, numeric: false): WritableComputedRef<number | T>;
export function useRouteQueryParam<T extends string | undefined>(name: string, def: T): WritableComputedRef<string | T>;
export function useRouteQueryParam(name: string): WritableComputedRef<string | undefined>;
export function useRouteQueryParam(name: string, def: string | number | undefined = undefined, numeric = false) {
  const router = useRouter();
  const route = useRoute();

  return computed({
    get() {
      const value = pop(route.query[name]);
      if (numeric && isDefined(value)) {
        return Number(value);
      }
      return value ?? def;
    },
    set(value) {
      router.push({
        query: {
          ...route.query,
          [name]: value,
        },
      });
    },
  });
}

/**
 * Wrapper per inject che lancia un'eccezione se il valore non è presente (non-optional)
 * @template T - Tipo del valore iniettato
 * @param key - InjectionKey o stringa per identificare l'injection
 * @param errorMessage - Messaggio di errore personalizzato (default: 'Missing required injection')
 * @returns Il valore iniettato (garantito non-undefined)
 * @throws Error se il valore non è stato fornito
 */
export function requireInject<T>(key: InjectionKey<T> | string, errorMessage: string | undefined): T {
  const value = inject(key);
  if (value === undefined) {
    throw new Error(errorMessage ?? 'Missing required injection');
  }
  return value;
}

/**
 * Crea un setting utente salvato nel localStorage con prefix opzionale e supporto per nomi dinamici
 * @template T - Tipo del valore del setting
 * @param name - Nome del setting (può essere reattivo)
 * @param initialValue - Valore iniziale/default
 * @param options - Opzioni con prefix opzionale per namespace il setting
 * @returns RemovableWritableComputedRef che persiste nel localStorage
 */
export function useUserSetting<T>(name: MaybeRefOrGetter<string | undefined>, initialValue: T, options: {
  prefix?: MaybeRefOrGetter<string | undefined>;
} = {}): RemovableWritableComputedRef<T> {
  const setting = ref(ref(initialValue)) as Ref<RemovableRef<T>>;

  const storageName = computed(() => {
    const prefix = toValue(options.prefix);
    const storageName = toValue(name);
    if ('prefix' in options) {
      return isDefined(prefix) && isDefined(storageName) ? `${prefix}.${storageName}` : undefined;
    }
    return storageName;
  });

  watch(() => [name, options.prefix], () => {
    if (!storageName.value) {
      setting.value = ref(initialValue) as RemovableRef<T>;
      return;
    }
    setting.value = useLocalStorage(storageName.value, initialValue, {
      listenToStorageChanges: false,
    });
  }, { immediate: true });

  return computed({
    get: () => toValue(toValue(setting)) ?? initialValue,
    set: (value: T | undefined) => (toValue(setting).value = value),
  }) as RemovableWritableComputedRef<T>;
}

/**
 * Wrapper per una funzione che traccia il suo stato di esecuzione (running/idle)
 * @template T - Tipo della funzione da wrappare
 * @param fn - Funzione da wrappare
 * @param options - Opzioni: preventMultiple per impedire esecuzioni concorrenti
 * @returns Oggetto con isRunning (ref booleano) e run (funzione wrapped)
 */
export function useFunctionRunningState<T extends (...args: any[]) => any>(fn: T, { preventMultiple } = { preventMultiple: false }) {
  const isRunning = ref(false);

  const run = (...args: ArgumentsType<T>): ReturnType<T> | undefined => {
    if (preventMultiple && isRunning.value) {
      return;
    }

    isRunning.value = true;

    let result: ReturnType<T> | undefined = undefined;
    try {
      result = fn(...args);
      if (result && (result as any) instanceof Promise) {
        return result.finally(() => (isRunning.value = false));
      }
      return result;
    } finally {
      if (!((result as any) instanceof Promise)) {
        isRunning.value = false;
      }
    }
  };

  return {
    isRunning,
    run,
  };
}
